/**
 * Per-photo performance stats, populated silently by the various processing
 * stages (metadata read, raw load/render, culling info/scoring, export).
 *
 * Exposed at `globalThis.__performanceStats` as a `Map<photoId, descriptor>`.
 * Both main-thread and worker realms get their own map (realm-local globals);
 * the perf-test sampler reads each realm separately and merges by photoId.
 *
 * NOTE: this module must remain realm-agnostic. It is imported by
 * `raw-adapter.ts`, which runs inside `ai.worker.ts` — pulling in the
 * Jotai store / projectAtom transitively pulls in `window`-touching code
 * and crashes the worker at import time. The project-id prefix that used
 * to live here was moved to the consumer (the perf report builder) which
 * does have main-thread access.
 *
 * All times are `performance.now()` (sub-ms, monotonic). All fields are
 * optional; readers should treat missing fields as "not yet measured".
 */

export type PerfStatTimingPhase =
	| "metadataRead"
	| "rawLoad"
	| "rawRender"
	| "cullingInfo"
	| "cullingScoring"
	| "exportBytes"
	| "exportEncodeOutput"

export interface PerformanceStatDescriptor {
	fileSize?: number
	rawBytesSize?: number
	name?: string
	width?: number
	height?: number
	metadataReadStartTime?: number
	metadataReadEndTime?: number
	rawLoadStartTime?: number
	rawLoadEndTime?: number
	rawRenderStartTime?: number
	rawRenderEndTime?: number
	cullingInfoStartTime?: number
	cullingInfoEndTime?: number
	cullingScoringStartTime?: number
	cullingScoringEndTime?: number
	exportBytesStartTime?: number
	exportBytesEndTime?: number
	exportEncodeOutputStartTime?: number
	exportEncodeOutputEndTime?: number
	rawLoadStatus?: string
}

declare global {
	// eslint-disable-next-line no-var
	var __performanceStats: Map<string, PerformanceStatDescriptor> | undefined
}

const getStats = (): Map<string, PerformanceStatDescriptor> => {
	if (!globalThis.__performanceStats) {
		globalThis.__performanceStats = new Map()
	}
	return globalThis.__performanceStats
}

const ensure = (photoId: string): PerformanceStatDescriptor => {
	const stats = getStats()
	let entry = stats.get(photoId)
	if (!entry) {
		entry = {}
		stats.set(photoId, entry)
	}
	return entry
}

export const startTrackingPerformanceValue = (
	photoId: string,
	phase: PerfStatTimingPhase,
): void => {
	if (!photoId) return
	const entry = ensure(photoId) as Record<string, unknown>
	entry[`${phase}StartTime`] = performance.now()
}

export const commitTrackingPerformanceValue = (
	photoId: string,
	phase: PerfStatTimingPhase,
): void => {
	if (!photoId) return
	const entry = ensure(photoId) as Record<string, unknown>
	entry[`${phase}EndTime`] = performance.now()
}

export const setPerformanceStat = <K extends keyof PerformanceStatDescriptor>(
	photoId: string,
	field: K,
	value: PerformanceStatDescriptor[K],
): void => {
	if (!photoId) return
	ensure(photoId)[field] = value
}
